// src/portals/creator/pages/WalletPage.js
import React, { useState } from "react";
import { mockCreator, ethiopianPaymentProviders } from "../../../data/mockData";
import Card from "../../../components/ui/Card";
import Button from "../../../components/ui/Button";
import {
  DollarSign,
  CreditCard,
  ArrowUpRight,
  ArrowDownLeft,
  Download,
  Filter,
  Activity,
  Plus,
  Wallet,
  X,
  Check
} from "lucide-react";

// --- Internal Components ---

const Modal = ({ isOpen, onClose, title, children }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        <div className="flex justify-between items-center p-4 border-b border-slate-100">
          <h3 className="font-bold text-lg text-slate-800">{title}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 transition-colors">
            <X size={20} />
          </button>
        </div>
        <div className="p-4">{children}</div>
      </div>
    </div>
  );
};

const ManageMethodsModal = ({ isOpen, onClose, methods, onAdd }) => {
  const [step, setStep] = useState("list"); // list, add
  const [newMethod, setNewMethod] = useState({ provider: "", account: "" });

  const handleAdd = () => {
    if (newMethod.provider && newMethod.account) {
      onAdd({ ...newMethod, id: Date.now() });
      setNewMethod({ provider: "", account: "" });
      setStep("list");
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={step === "list" ? "Payment Methods" : "Add Payment Method"}>
      <div className="mb-4 bg-slate-50 p-3 rounded-lg flex items-center justify-between border border-slate-100">
        <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Powered by</span>
        <div className="flex items-center gap-1">
          <span className="font-black text-indigo-600 text-lg tracking-tighter">INVERSE</span>
        </div>
      </div>

      {step === "list" ? (
        <div className="space-y-4">
          {methods.length === 0 ? (
            <div className="text-center py-6 text-slate-500 text-sm">No payment methods linked yet.</div>
          ) : (
            <div className="space-y-2">
              {methods.map((m) => (
                <div key={m.id} className="flex items-center justify-between p-3 border border-slate-100 rounded-lg hover:border-indigo-100 transition-colors">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-indigo-50 rounded-full flex items-center justify-center text-indigo-600">
                      <Wallet size={18} />
                    </div>
                    <div>
                      <div className="font-bold text-slate-700 text-sm">{m.provider}</div>
                      <div className="text-xs text-slate-500">{m.account}</div>
                    </div>
                  </div>
                  <div className="text-xs font-medium text-green-600 bg-green-50 px-2 py-1 rounded-full">Active</div>
                </div>
              ))}
            </div>
          )}
          <Button onClick={() => setStep("add")} className="w-full mt-2">
            <Plus size={16} className="mr-2" /> Add New Method
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Provider</label>
            <div className="relative">
              <select
                className="w-full text-sm border border-slate-200 rounded-lg p-3 bg-white focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 appearance-none outline-none transition-shadow"
                value={newMethod.provider}
                onChange={(e) => setNewMethod({ ...newMethod, provider: e.target.value })}
              >
                <option value="">Select Provider...</option>
                <optgroup label="Banks">
                  {ethiopianPaymentProviders.banks.map((b) => (
                    <option key={b} value={b}>{b}</option>
                  ))}
                </optgroup>
                <optgroup label="Mobile Money">
                  {ethiopianPaymentProviders.mobileMoney.map((m) => (
                    <option key={m} value={m}>{m}</option>
                  ))}
                </optgroup>
              </select>
              <div className="absolute right-3 top-3.5 pointer-events-none text-slate-400">
                <ArrowDownLeft size={16} className="-rotate-45" />
              </div>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Account Number / Phone</label>
            <input
              type="text"
              className="w-full text-sm border border-slate-200 rounded-lg p-3 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-shadow"
              placeholder="e.g. 100012345678 or 0911..."
              value={newMethod.account}
              onChange={(e) => setNewMethod({ ...newMethod, account: e.target.value })}
            />
          </div>
          <div className="flex gap-2 pt-2">
            <Button variant="outline" onClick={() => setStep("list")} className="flex-1 py-2.5">
              Cancel
            </Button>
            <Button onClick={handleAdd} disabled={!newMethod.provider || !newMethod.account} className="flex-1 py-2.5">
              Link Account
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
};

const TransactionModal = ({ isOpen, onClose, type, methods, onConfirm }) => {
  const [amount, setAmount] = useState("");
  const [methodId, setMethodId] = useState("");
  const [isSuccess, setIsSuccess] = useState(false);

  const handleSubmit = () => {
    if (!amount || !methodId) return;
    setIsSuccess(true);
    setTimeout(() => {
      onConfirm({ amount: Number(amount), methodId, type });
      setIsSuccess(false);
      setAmount("");
      setMethodId("");
      onClose();
    }, 1500);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={type === "topup" ? "Top Up Wallet" : "Withdraw Funds"}>
      {isSuccess ? (
        <div className="py-8 text-center animate-in zoom-in duration-300">
          <div className="w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
            <Check size={32} strokeWidth={3} />
          </div>
          <h4 className="text-xl font-bold text-slate-800 mb-2">Success!</h4>
          <p className="text-slate-500">Your transaction is being processed.</p>
        </div>
      ) : (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Select Account</label>
            <div className="relative">
              <select
                className="w-full text-sm border border-slate-200 rounded-lg p-3 bg-white focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 appearance-none outline-none transition-shadow"
                value={methodId}
                onChange={(e) => setMethodId(e.target.value)}
              >
                <option value="">Select...</option>
                {methods.map((m) => (
                  <option key={m.id} value={m.id}>{m.provider} - {m.account}</option>
                ))}
              </select>
              <div className="absolute right-3 top-3.5 pointer-events-none text-slate-400">
                <ArrowDownLeft size={16} className="-rotate-45" />
              </div>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Amount (ETB)</label>
            <div className="relative">
              <input
                type="number"
                className="w-full text-sm border border-slate-200 rounded-lg p-3 pr-12 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-shadow"
                placeholder="0.00"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
              />
              <span className="absolute right-3 top-3.5 text-slate-400 text-xs font-medium">ETB</span>
            </div>
          </div>
          <div className="pt-2">
            <Button 
              onClick={handleSubmit} 
              disabled={!amount || !methodId} 
              className={`w-full ${type === "topup" ? "bg-green-600 hover:bg-green-700" : "bg-indigo-600 hover:bg-indigo-700"}`}
            >
              {type === "topup" ? "Confirm Top Up" : "Confirm Withdrawal"}
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
};


const PayoutScheduleModal = ({ isOpen, onClose, currentSchedule, currentAccountId, methods, onSave }) => {
  const [schedule, setSchedule] = useState(currentSchedule);
  const [accountId, setAccountId] = useState(currentAccountId || (methods[0]?.id || ""));
  
  const schedules = [
    { id: "Monthly", label: "Monthly", description: "Get paid on the 1st of every month." },
    { id: "Quarterly", label: "Quarterly", description: "Get paid on Jan 1, Apr 1, Jul 1, Oct 1." },
    { id: "Yearly", label: "Yearly", description: "Get paid once a year on Jan 1." },
  ];

  const handleSave = () => {
    onSave(schedule, accountId);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Payout Schedule">
      <div className="space-y-6">
        {/* Account Selection */}
        <div>
           <label className="block text-sm font-medium text-slate-700 mb-2">Payout To</label>
           <div className="relative">
              <select
                className="w-full text-sm border border-slate-200 rounded-lg p-3 bg-white focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 appearance-none outline-none transition-shadow"
                value={accountId}
                onChange={(e) => setAccountId(e.target.value)}
              >
                {methods.map((m) => (
                  <option key={m.id} value={m.id}>{m.provider} - {m.account}</option>
                ))}
              </select>
              <div className="absolute right-3 top-3.5 pointer-events-none text-slate-400">
                <ArrowDownLeft size={16} className="-rotate-45" />
              </div>
            </div>
        </div>

        {/* Schedule Selection */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">Frequency</label>
          <div className="space-y-2">
            {schedules.map((s) => (
              <div 
                key={s.id}
                onClick={() => setSchedule(s.id)}
                className={`p-3 rounded-lg border cursor-pointer transition-all ${
                  schedule === s.id 
                    ? "border-indigo-600 bg-indigo-50 ring-1 ring-indigo-600" 
                    : "border-slate-200 hover:border-slate-300 hover:bg-slate-50"
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className={`mt-0.5 w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                    schedule === s.id ? "border-indigo-600" : "border-slate-300"
                  }`}>
                    {schedule === s.id && <div className="w-2 h-2 rounded-full bg-indigo-600" />}
                  </div>
                  <div>
                    <div className={`font-bold text-sm ${schedule === s.id ? "text-indigo-900" : "text-slate-700"}`}>
                      {s.label}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">{s.description}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="pt-2">
          <Button onClick={handleSave} className="w-full bg-indigo-600 hover:bg-indigo-700">
            Save Changes
          </Button>
        </div>
      </div>
    </Modal>
  );
};


// --- Main Page Component ---

const WalletPage = () => {
  const { wallet } = mockCreator;
  const [filter, setFilter] = useState("All");
  
  // State for modals
  const [isManageOpen, setManageOpen] = useState(false);
  const [isTopUpOpen, setTopUpOpen] = useState(false);
  const [isWithdrawOpen, setWithdrawOpen] = useState(false);
  const [isScheduleOpen, setScheduleOpen] = useState(false);

  // Settings
  const [payoutSchedule, setPayoutSchedule] = useState("Monthly");

  // Initial mock payment methods
  const [paymentMethods, setPaymentMethods] = useState([
    { id: 1, provider: "Commercial Bank of Ethiopia", account: "1000****4589" }
  ]);

  // Mock transactions
  const [transactions, setTransactions] = useState([
    { id: 1, type: "income", description: "Subscription Revenue (Oct)", amount: 4500, date: "2024-10-25", status: "Completed" },
    { id: 2, type: "income", description: "Merch Sales: T-Shirt Drop", amount: 12500, date: "2024-10-22", status: "Completed" },
    { id: 3, type: "payout", description: "Withdrawal to CBE", amount: -8000, date: "2024-10-15", status: "Processed" },
  ]);

  const filteredTransactions = filter === "All" ? transactions : transactions.filter(t => 
    filter === "Income" ? t.amount > 0 : t.amount < 0
  );

  const handleAddMethod = (method) => {
    setPaymentMethods([...paymentMethods, method]);
  };

  const handleTransaction = ({ amount, type }) => {
    // Optimistic update for demo
    const newTx = {
      id: Date.now(),
      type: type === "topup" ? "income" : "payout",
      description: type === "topup" ? "Wallet Top Up" : "Withdrawal Request",
      amount: type === "topup" ? amount : -amount,
      date: new Date().toISOString().split('T')[0],
      status: type === "topup" ? "Completed" : "Pending",
    };
    setTransactions([newTx, ...transactions]);
  };

  return (
    <div>
      <div className="flex flex-col @md:flex-row items-start @md:items-center justify-between mb-8 gap-4">
        <div>
          <h1 className="text-2xl @sm:text-3xl font-bold text-slate-800">Wallet</h1>
          <p className="text-slate-500 mt-1 text-sm">
            Manage your earnings and payouts securely.
          </p>
        </div>
        <Button variant="outline" className="w-full @md:w-auto text-xs @sm:text-sm">
          <Download size={16} className="mr-2" /> Export Statement
        </Button>
      </div>

      <div className="flex flex-col @lg:grid @lg:grid-cols-3 gap-6 mb-8">
        {/* Balance Card - Main Focus */}
        <div className="@lg:col-span-2 bg-gradient-to-br from-indigo-900 to-slate-900 rounded-2xl p-6 @sm:p-8 text-white shadow-xl relative overflow-hidden">
          <div className="absolute top-0 right-0 p-32 bg-indigo-500 opacity-10 rounded-full -mr-16 -mt-16 blur-2xl"></div>
          
          <div className="relative z-10">
            <p className="text-indigo-200 font-medium mb-1 text-sm @sm:text-base">Total Balance</p>
            <h2 className="text-3xl @sm:text-5xl font-bold mb-8">{(wallet?.currentBalanceETB || 0).toLocaleString()} ETB</h2>
            
            <div className="grid grid-cols-2 @sm:flex gap-3 @sm:gap-4">
              <Button 
                onClick={() => setTopUpOpen(true)}
                className="bg-green-500 hover:bg-green-600 text-white font-bold border-none flex-1 @sm:flex-none justify-center"
              >
                <Plus size={18} className="mr-2" /> Top Up
              </Button>
              <Button 
                onClick={() => setWithdrawOpen(true)}
                className="bg-white text-indigo-900 hover:bg-indigo-50 font-bold border-none flex-1 @sm:flex-none justify-center"
              >
                <ArrowUpRight size={18} className="mr-2" /> Withdraw
              </Button>
              <Button 
                variant="outline" 
                onClick={() => setManageOpen(true)}
                className="col-span-2 @sm:col-span-1 border-white/20 text-white hover:bg-white/10 flex-1 @sm:flex-none justify-center"
              >
                 Manage Methods
              </Button>
            </div>
          </div>
        </div>

        {/* Pending Card */}
        <Card className="flex flex-col justify-center">
           <div className="flex items-center gap-3 mb-2 text-slate-500 font-medium">
             <div className="p-2 bg-amber-100 text-amber-600 rounded-lg"><Activity size={18} /></div>
             Pending Clearance
           </div>
           <div className="text-3xl font-bold text-slate-800 mb-1">{(wallet?.pendingBalance || 0).toLocaleString()} ETB</div>
           <p className="text-sm text-slate-400">Available on Nov 1, 2024</p>
           
           <div className="mt-6 pt-6 border-t border-slate-100">
             <div className="flex justify-between items-center text-sm">
                <span className="text-slate-500">Next Payout</span>
                <button 
                  onClick={() => setScheduleOpen(true)}
                  className="font-bold text-indigo-600 hover:text-indigo-700 bg-indigo-50 hover:bg-indigo-100 px-2.5 py-1 rounded text-xs transition-colors"
                >
                  Auto: {payoutSchedule}
                </button>
             </div>
           </div>
        </Card>
      </div>

      {/* Transactions */}
      <Card className="p-0 overflow-hidden">
        <div className="p-6 border-b border-slate-100 flex justify-between items-center">
          <h3 className="text-lg font-bold text-slate-800">Recent Transactions</h3>
          <div className="flex gap-2">
            {["All", "Income", "Payouts"].map(f => (
               <button 
                 key={f}
                 onClick={() => setFilter(f)}
                 className={`px-3 py-1.5 text-sm rounded-lg font-medium transition-colors ${
                   filter === f ? "bg-indigo-100 text-indigo-700" : "text-slate-500 hover:bg-slate-50"
                 }`}
               >
                 {f}
               </button>
            ))}
          </div>
        </div>
        
        <div className="divide-y divide-slate-100">
          {filteredTransactions.map((t) => (
            <div key={t.id} className="p-4 hover:bg-slate-50 transition-colors flex items-center justify-between gap-3">
              <div className="flex items-center gap-3 @sm:gap-4 min-w-0">
                <div className={`p-2 @sm:p-3 rounded-full flex-shrink-0 ${t.amount > 0 ? "bg-green-100 text-green-600" : "bg-slate-100 text-slate-500"}`}>
                  {t.amount > 0 ? <ArrowDownLeft size={16} className="@sm:w-5 @sm:h-5" /> : <ArrowUpRight size={16} className="@sm:w-5 @sm:h-5" />}
                </div>
                <div className="min-w-0">
                  <div className="font-bold text-slate-800 text-sm @sm:text-base truncate">{t.description}</div>
                  <div className="text-[10px] @sm:text-xs text-slate-500 truncate">{t.date} • {t.status}</div>
                </div>
              </div>
              <div className={`font-bold text-sm @sm:text-base flex-shrink-0 ${t.amount > 0 ? "text-green-600" : "text-slate-800"}`}>
                {t.amount > 0 ? "+" : ""}{t.amount.toLocaleString()} ETB
              </div>
            </div>
          ))}
        </div>
        <div className="p-4 border-t border-slate-100 text-center">
          <button className="text-indigo-600 font-medium text-sm hover:underline">View All Transactions</button>
        </div>
      </Card>

      {/* Modals */}
      <ManageMethodsModal 
        isOpen={isManageOpen} 
        onClose={() => setManageOpen(false)}
        methods={paymentMethods}
        onAdd={handleAddMethod}
      />

      <TransactionModal
        isOpen={isTopUpOpen}
        onClose={() => setTopUpOpen(false)}
        type="topup"
        methods={paymentMethods}
        onConfirm={handleTransaction}
      />

      <TransactionModal
        isOpen={isWithdrawOpen}
        onClose={() => setWithdrawOpen(false)}
        type="withdraw"
        methods={paymentMethods}
        onConfirm={handleTransaction}
      />

      <PayoutScheduleModal
        isOpen={isScheduleOpen}
        onClose={() => setScheduleOpen(false)}
        currentSchedule={payoutSchedule}
        methods={paymentMethods}
        onSave={(newSchedule) => setPayoutSchedule(newSchedule)} // Account ID saving would happen here in a real app
      />
    </div>
  );
};

export default WalletPage;
